import React, { Component } from 'react';
import { connect, } from 'react-redux';
import { Container, Modal, } from 'react-bootstrap';
import { withRouter } from './withRouter';
import { Link, } from 'react-router-dom';
import PageSEO from 'components/Utlility/PageSEO';
import { SEO_CONFIG } from 'constants/SeoConfig';
import AuthService from 'services/auth';
import ProfileService from 'services/profile';
import Login from './Forms/Login';
import FormPage from './Forms/FormPage';
import styles from "./OnTheGoStyles.module.scss";
import GreenUnderline from 'images/Promotions/GreenUnderline.png';

const CON_ARTIST_URL = process.env.REACT_APP_CONARTIST_URL || "https://conartist.io";

class OnTheGo extends Component {
    static displayName = OnTheGo.name;

    // ownsGames: null = unknown (not logged in / still fetching / fetch failed),
    // true/false = definitive answer from get-profile. Only an explicit `false`
    // sends the user to the shop — an unknown never blocks the play-through.
    // showLoginModal: the single CTA opens an inline login when the visitor isn't
    // signed in, then resolves ownership and continues without a page change.
    // showNeedsGameModal: when ownership resolves to "owns nothing", we surface a
    // soft prompt to the shop rather than yanking them off the page.
    state = { ownsGames: null, showLoginModal: false, showNeedsGameModal: false };

    componentDidMount() {
        this.fetchOwnership();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.auth?.isLoggedIn !== this.props.auth?.isLoggedIn) {
            this.setState({ ownsGames: null });
            this.fetchOwnership();
        }
    }

    // Ownership gate (existing endpoint — get-profile carries totalGamesOwned).
    // Returns true/false when the profile answers, null otherwise (unknown).
    // getProfile never rejects; on error it returns { Status: "Error" }, which
    // leaves ownsGames at null and the CTA on its play-through fallback.
    resolveOwnership = async () => {
        const profile = await ProfileService.getProfile();
        if (typeof profile?.totalGamesOwned === "number") {
            const owns = profile.totalGamesOwned > 0;
            this.setState({ ownsGames: owns });
            return owns;
        }
        return null;
    }

    // Prefetch on mount / login so the CTA copy reflects the visitor's state.
    fetchOwnership = async () => {
        if (!this.props.auth?.isLoggedIn) return;
        await this.resolveOwnership();
    }

    // Copy hint only: logged-out visitors are pitched the free trial and, once
    // signed in, an explicit "owns nothing" swaps the CTA to the shop. The click
    // handler owns the real routing — this just picks the wording.
    get needsShop() {
        if (!this.props.auth?.isLoggedIn) return true;
        return this.state.ownsGames === false;
    }

    // The single CTA:
    //   1) logged-in owner        → straight to Con Artist
    //   2) logged out             → inline login, then re-run this from the modal
    //   3) logged in, owns nothing → the "you need a game" prompt
    // An unknown ownership answer (fetch failed) falls through to the play-through
    // rather than blocking a genuine owner.
    playOnTheGo = async () => {
        if (!this.props.auth?.isLoggedIn) {
            this.setState({ showLoginModal: true });
            return;
        }
        let owns = this.state.ownsGames;
        if (owns === null) owns = await this.resolveOwnership();
        this.proceedAfterOwnership(owns);
    }

    // Called when the inline login modal closes. On a successful login we resolve
    // ownership fresh, then continue the play-through from the same click.
    handleLoginClose = async (loggedIn = false) => {
        this.setState({ showLoginModal: false });
        if (!loggedIn) return;
        const owns = await this.resolveOwnership();
        this.proceedAfterOwnership(owns);
    }

    // Owners (and unknowns — never block a genuine owner) head to Con Artist; a
    // confirmed "owns nothing" gets the soft shop prompt instead of a hard redirect.
    proceedAfterOwnership = (owns) => {
        if (owns === false) {
            this.setState({ showNeedsGameModal: true });
            return;
        }
        this.goToConArtist();
    }

    // Logged-in owners get a single-use handover code so conartist.io can sign
    // them straight in. Any failure falls back to the plain URL — the CTA
    // must never block. Same-tab navigation (not window.open) because the login
    // path lands here several async hops from the click, where a new-tab open
    // would be popup-blocked — and a full handover is the intent anyway.
    goToConArtist = async () => {
        try {
            const data = await AuthService.createHandoverCode();
            if (data?.code) {
                window.location.href = `${CON_ARTIST_URL}/?gcHandover=${encodeURIComponent(data.code)}`;
                return;
            }
        } catch (error) {
            console.log(error);
        }
        window.location.href = CON_ARTIST_URL;
    }

    // Second CTA: a guest joining someone else's table. Deliberately outside the
    // login/ownership gate above — a joiner needs neither an account nor a game,
    // only the code their host reads out. No handover code either, so nothing
    // about the visitor's game.city login travels with them. ?join=1 opens
    // conartist.io straight on its join screen with an empty code field.
    joinAGame = () => {
        window.location.href = `${CON_ARTIST_URL}/?join=1`;
    }

    render() {
        // One CTA for everyone — the click handler decides where it lands
        // (Con Artist, login, or the shop). needsShop only tunes the surrounding copy.
        const needsShop = this.needsShop;
        const ctaLabel = <>Play Con Artist on the Go &rarr;</>;
        // The joiner's route in, shown in every auth state (see joinAGame).
        const joinCta = (
            <div className={styles.joinCta}>
                <p className={styles.joinLead}>If you're joining someone else's game, click below to go straight there — you don't need an account or to own any games to play.</p>
                {/* Blue in both placements: the hero's primary is red and the
                    footer's is orange, so the shared "alternate" would have
                    collided with the footer CTA. Blue keeps the join route
                    looking like one consistent thing on both. */}
                <button className={`${styles.button} ${styles.blueAlt} ${styles.joinBtn}`} onClick={this.joinAGame}>Join Game</button>
            </div>
        );
        return (
            <article>
                <PageSEO {...SEO_CONFIG['/go']} />
                <div className={styles.cloudBG} />

                <section className={styles.band}>
                    <Container>

                        {/* Hero */}
                        <div className={`${styles.card} ${styles.hero}`}>
                            <div className={styles.eyebrowWrap}>
                                <h3 className={styles.eyebrow}>New &middot; Summer Trial</h3>
                                <img src={GreenUnderline} className={styles.greenUnderline} alt="" />
                            </div>
                            <h1>{SEO_CONFIG['/go'].h1}</h1>
                            <p className={styles.subhead}>Game night, minus the big screen. Play on your phones, wherever you are, park, pub, beach, back seat of the car. Just like normal Game.City, only one player needs an account, everyone else joins with the room code.</p>
                            <button className={styles.button} onClick={this.playOnTheGo}>{ctaLabel}</button>
                            <p className={styles.subLine}>
                                {needsShop
                                    ? <>Own any Game.City game and Con Artist on the Go is free all summer. Tap play and we'll get you signed in.</>
                                    : <>Free all summer if you own a Game.City game.</>}
                            </p>
                            {joinCta}
                        </div>

                        {/* What it is */}
                        <div className={styles.card}>
                            <h2>No telly? No problem.</h2>
                            <p>Our games usually live on the big screen. But sometimes there isn't one, and the fun shouldn't have to wait. Game.City on the Go brings our games to your phone, so you can play wherever you and your people happen to be. Same Game.City, pocket-sized.</p>

                            <hr />

                            {/* The trial, what you can play now */}
                            <h2>First up: Con Artist</h2>
                            <p>We're starting with Con Artist, the drawing-and-bluffing game where one of you has no idea what they're meant to be drawing and has to blag it. It's the first game to leave the big screen, and it's a summer trial, so play it, love it, and tell us what you think.</p>

                            <hr />

                            {/* Who gets it / how it's free */}
                            <h2>Free this summer, because you're already one of us</h2>
                            <p>If you own any Game.City game, Con Artist on the Go is free for you all summer. Just log in with your usual Game.City account, if you're already signed in here, we'll take you straight through. Nothing to pay, no code to enter.</p>
                            <p>Don't own a game yet? <Link to="/shop">Grab one from the shop</Link> and you're in.</p>

                            <hr />

                            {/* How it works */}
                            <h2>How it works</h2>
                            <ol className={styles.steps}>
                                <li><p>Tap play, we'll send you to the on-the-go game</p></li>
                                <li><p>Log in, your Game.City account works here too</p></li>
                                <li><p>Everyone joins on their phones, no app, no console, no telly needed</p></li>
                            </ol>

                            <hr />

                            {/* What's next */}
                            <h2>More games to follow</h2>
                            <p>Con Artist is just the start. Depending on how the summer goes, we'll bring more of our games on the go, so keep an eye out.</p>
                        </div>

                    </Container>
                </section>

                <div className={styles.cityScape} />

                {/* Footer CTA */}
                <section className={`${styles.band} ${styles.small}`}>
                    <div className={styles.footerCta}>
                        <p>{needsShop ? "Want in?" : "Ready to play?"}</p>
                        <button className={`${styles.button} ${styles.alternate}`} onClick={this.playOnTheGo}>{ctaLabel}</button>
                        <p className={styles.subLine}>Free all summer for Game.City owners.</p>
                        {joinCta}
                    </div>
                </section>

                {/* Inline login — a logged-out CTA click resolves ownership and
                    continues (Con Artist or shop prompt) without leaving the page. */}
                <Modal show={this.state.showLoginModal} onHide={this.handleLoginClose} centered className="autoWidthModal">
                    <FormPage is="login" modal={true}>
                        <Login modal={true} closeModal={this.handleLoginClose} />
                    </FormPage>
                </Modal>

                {/* Soft shop prompt — shown when a signed-in visitor owns no games,
                    instead of an abrupt jump to /shop. */}
                <Modal show={this.state.showNeedsGameModal} onHide={() => this.setState({ showNeedsGameModal: false })} centered>
                    <div className={styles.needsGameModal}>
                        <h2>You'll need a game first</h2>
                        <p>Con Artist on the Go is free all summer for anyone who owns a Game.City game. Grab one from the shop and you're straight in.</p>
                        <button className={styles.button} onClick={() => this.props.navigate('/shop')}>Go to the shop &rarr;</button>
                        <button className={`${styles.button} ${styles.alternate}`} onClick={() => this.setState({ showNeedsGameModal: false })}>Maybe later</button>
                    </div>
                </Modal>
            </article>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        auth: state.auth,
    };
}

export default connect(mapStateToProps)(withRouter(OnTheGo));
